/* eslint-disable react-hooks/refs -- Ref-backed preview and publish queues are only touched by effects and event handlers. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError, payload } from "./lib/api";
import { uploadMedia } from "./lib/upload-media";
import { isAutoReloading, markUnsaved } from "./lib/auto-update";
import {
  inferLinkKind,
  linkInputValue,
  linkPlaceholder,
  linkProblem,
  normalizeLink,
  type LinkKind,
} from "./lib/link-editor";
import { useAccessibleDialog } from "./lib/useAccessibleDialog";
import type { MediaAsset, Page, Site } from "./types";
import HeroQuickEditor from "./HeroQuickEditor";
import SectionBuilder, { type BuilderRequest, type ContentBlock, type PageSection } from "./SectionBuilder";
import universeMark from "../../Universe/favicon-512.png";

function samePath(a: string | undefined, b: string | undefined) {
  const clean = (value: string | undefined) => {
    let path = value || "/";
    if (path.endsWith("/index.html")) path = path.slice(0, -10);
    else if (path.endsWith(".html")) path = path.slice(0, -5);
    while (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
    return path.replace("/?", "?") || "/";
  };
  return clean(a) === clean(b);
}

interface ContentField {
  key: string;
  label: string;
  type: "text" | "textarea" | "image" | "gallery";
  mediaType: "image" | "video" | null;
  group: string;
  value: string | string[];
  previousValue: string | string[] | null;
  defaultValue?: string | string[];
  auto?: boolean;
  purpose?: "content" | "alt" | "link";
  pagePath?: string;
  i18nKey?: string | null;
  frame?: PhotoFrame | null;
}
type PhotoFrame = { x: number; y: number; ratio: "site" | "landscape" | "portrait" };

function readFrame(value: unknown): PhotoFrame | null {
  if (!value || typeof value !== "object") return null;
  const frame = value as Partial<PhotoFrame>;
  const x = Number(frame.x);
  const y = Number(frame.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (frame.ratio !== "site" && frame.ratio !== "landscape" && frame.ratio !== "portrait") return null;
  return {
    x: Math.min(100, Math.max(0, Math.round(x))),
    y: Math.min(100, Math.max(0, Math.round(y))),
    ratio: frame.ratio,
  };
}

function framesFrom(fields: ContentField[]): Record<string, PhotoFrame> {
  return Object.fromEntries(
    fields.flatMap((field) => {
      const frame = readFrame(field.frame);
      return frame ? [[field.key, frame]] : [];
    }),
  );
}
interface PendingChanges {
  updates: Record<string, string | string[]>;
  frames: Record<string, PhotoFrame>;
  count: number;
  updatedAt: string | null;
}
const NO_PENDING: PendingChanges = { updates: {}, frames: {}, count: 0, updatedAt: null };
interface ContentVersion {
  id: string;
  number: number;
  createdAt: string;
  author: string;
  changedFields: string[];
  restoredFrom: string | null;
}

function isLockedCredit(field: ContentField): boolean {
  const parts = [field.label, field.defaultValue, field.value]
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.toLowerCase().replace(/\s+/g, " ").trim().replace(/^(?:a|span|div|em|strong|small|p):\s*/, ""));
  return parts.some((text) => {
    const signature = text.length <= 180 && (
      /realizzato da[^a-z0-9]{0,16}universe/.test(text) ||
      /made by[^a-z0-9]{0,16}universe/.test(text) ||
      /erstellt von[^a-z0-9]{0,16}universe/.test(text) ||
      /r[eé]alis[eé] par[^a-z0-9]{0,16}universe/.test(text)
    );
    if (signature) return true;
    return (
      text.length <= 48 &&
      (text === "realizzato da" ||
        text === "made by" ||
        text === "erstellt von" ||
        text === "réalisé par" ||
        text === "realise par" ||
        text === "universe" ||
        text === "∞" ||
        text === "∞ universe")
    );
  });
}

function absoluteMedia(value: string, liveUrl: string): string {
  if (!value) return "";
  if (value.startsWith("/builder-media/") || value.startsWith("/media/")) {
    return new URL(value, window.location.origin).toString();
  }
  try {
    return new URL(value, `${liveUrl}/`).toString();
  } catch {
    return value;
  }
}

export default function SafeContentEditor({ canEdit }: { canEdit: boolean }) {
  const { siteId = "", pageId = "" } = useParams();
  const navigate = useNavigate();
  const [siteName, setSiteName] = useState("");
  const [liveUrl, setLiveUrl] = useState("");
  const [publicUrl, setPublicUrl] = useState("");
  const [liveConnected, setLiveConnected] = useState(true);
  const [fields, setFields] = useState<ContentField[]>([]);
  const [pages, setPages] = useState<Page[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string | string[]>>({});
  const [group, setGroup] = useState("");
  const [busy, setBusy] = useState("");
  const flushTextRef = useRef<() => Promise<void>>(async () => {});
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [previewKey, setPreviewKey] = useState(0);
  const [frames, setFrames] = useState<Record<string, PhotoFrame>>({});
  const [versions, setVersions] = useState<ContentVersion[]>([]);
  const [showVersions, setShowVersions] = useState(false);
  const [selectedKey, setSelectedKey] = useState("");
  const [linkKinds, setLinkKinds] = useState<Record<string, LinkKind>>({});
  const [media, setMedia] = useState<MediaAsset[]>([]);
  const [mediaTargetKey, setMediaTargetKey] = useState("");
  const [mediaSearch, setMediaSearch] = useState("");
  const [viewport, setViewport] = useState<"desktop" | "tablet" | "mobile">(
    "desktop",
  );
  const [pane, setPane] = useState<"work" | "preview">("work");
  const [hasMenu, setHasMenu] = useState(false);
  const [managedCount, setManagedCount] = useState(0);
  const [hasEscape, setHasEscape] = useState(false);
  const [heroOpen, setHeroOpen] = useState(false);
  const [heroSlug, setHeroSlug] = useState("");
  const [pending, setPending] = useState<PendingChanges>(NO_PENDING);
  const [publishing, setPublishing] = useState(false);
  const [publishedNotice, setPublishedNotice] = useState("");
  const previewRef = useRef<HTMLIFrameElement>(null);
  const [pageSections, setPageSections] = useState<PageSection[]>([]);
  const [builderRequest, setBuilderRequest] = useState<BuilderRequest | null>(null);
  const [builderActive, setBuilderActive] = useState(false);
  const liveUrlRef = useRef("");
  const pendingRef = useRef<PendingChanges>(NO_PENDING);
  const fieldsRef = useRef<ContentField[]>([]);
  const textTimers = useRef<Record<string, { timer: number; run: () => Promise<void> }>>({});
  const persistQueueRef = useRef<Promise<void>>(Promise.resolve());
  const historyDialogRef = useAccessibleDialog<HTMLElement>(
    showVersions,
    () => setShowVersions(false),
  );
  const mediaDialogRef = useAccessibleDialog<HTMLElement>(
    Boolean(mediaTargetKey),
    () => setMediaTargetKey(""),
  );
  useEffect(() => {
    Promise.all([
      api<{
        siteName: string;
        liveUrl: string;
        publicUrl: string;
        liveConnected: boolean;
        fields: ContentField[];
        pending?: PendingChanges;
      }>(`/api/sites/${siteId}/content-fields`),
      api<{ pages: Page[] }>(`/api/sites/${siteId}/pages`),
      api<{ site: Site }>(`/api/sites/${siteId}`),
    ])
      .then(([result, pageResult, siteResult]) => {
        const staged = result.pending || NO_PENDING;
        setSiteName(result.siteName);
        setLiveUrl(result.liveUrl);
        setPublicUrl(result.publicUrl);
        setLiveConnected(result.liveConnected);
        setFields(result.fields);
        setPages(pageResult.pages);
        setHasMenu(Boolean(siteResult.site.modules?.includes("restaurant_menu")));
        setHasEscape(Boolean(siteResult.site.modules?.includes("escape_bookings")));
        setPending(staged);
        setDrafts(
          Object.fromEntries(
            result.fields.map((field) => [field.key, staged.updates[field.key] ?? field.value]),
          ),
        );
        setLinkKinds(
          Object.fromEntries(
            result.fields
              .filter((field) => field.purpose === "link" && typeof field.value === "string")
              .map((field) => [field.key, inferLinkKind(String(staged.updates[field.key] ?? field.value))]),
          ),
        );
        setFrames({ ...framesFrom(result.fields), ...staged.frames });
        setGroup(result.fields[0]?.group || "Generale");
      })
      .catch((cause) => setError(cause.message));
  }, [siteId]);
  useEffect(() => {
    liveUrlRef.current = liveUrl;
  }, [liveUrl]);
  // Con un errore a schermo o un salvataggio in corso la pagina non si aggiorna da sola.
  useEffect(() => {
    markUnsaved("content-editor", Boolean(error) || Boolean(busy) || publishing);
    return () => markUnsaved("content-editor", false);
  }, [error, busy, publishing]);
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);
  useEffect(() => {
    const root = document.documentElement;
    const lock = () => {
      const height = window.visualViewport?.height || window.innerHeight;
      root.style.setProperty("--cms-vvh", `${Math.round(height)}px`);
    };
    lock();
    window.visualViewport?.addEventListener("resize", lock);
    window.addEventListener("orientationchange", lock);
    return () => {
      window.visualViewport?.removeEventListener("resize", lock);
      window.removeEventListener("orientationchange", lock);
      root.style.removeProperty("--cms-vvh");
    };
  }, []);
  useEffect(() => {
    if (!pending.count) return;
    // Le bozze sono già sul server: se ricarica l'aggiornamento automatico non serve chiedere conferma.
    const warn = (event: BeforeUnloadEvent) => { if (!isAutoReloading()) event.preventDefault(); };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [pending.count]);
  // Uscendo dall'editor, i testi scritti da meno di 700 ms partono subito.
  useEffect(() => () => { void flushTextRef.current(); }, []);
  const currentPage = pages.find((page) => page.id === pageId) || pages.find((page) => page.isHome) || pages[0];
  const currentPath = currentPage?.isHome ? "/" : `/${currentPage?.slug || ""}`;
  const virtualSlug = currentPage?.id?.startsWith("exp-") ? (currentPage.slug.split("evento=")[1] || "") : "";
  useEffect(() => {
    if (virtualSlug) { setHeroSlug(virtualSlug); setHeroOpen(true); }
  }, [virtualSlug]);
  function paintPreview(
    field: ContentField,
    value: string | string[],
    frame?: PhotoFrame | null,
  ) {
    const origin = liveUrlRef.current;
    const win = previewRef.current?.contentWindow;
    if (!origin || !win) return;
    win.postMessage(
      {
        type: "universe-cms-apply",
        field: { key: field.key, type: field.type, purpose: field.purpose, value, frame: frame || null },
      },
      new URL(origin).origin,
    );
  }
  function paintAllPending() {
    const origin = liveUrlRef.current;
    const win = previewRef.current?.contentWindow;
    if (!origin || !win) return;
    const staged = pendingRef.current;
    const known = new Map(fieldsRef.current.map((field) => [field.key, field]));
    const keys = new Set([...Object.keys(staged.updates), ...Object.keys(staged.frames)]);
    const payload = [...keys].flatMap((key) => {
      const field = known.get(key);
      if (!field) return [];
      return [{
        key: field.key,
        type: field.type,
        purpose: field.purpose,
        value: staged.updates[key] ?? field.value,
        frame: staged.frames[key] ?? readFrame(field.frame),
      }];
    });
    if (!payload.length) return;
    win.postMessage({ type: "universe-cms-apply-all", fields: payload }, new URL(origin).origin);
  }
  const persistField = useCallback(
    async (
      field: ContentField,
      value: string | string[],
      frame?: PhotoFrame | null,
    ) => {
      if (!canEdit) return;
      const execute = async () => {
        setBusy(field.key);
        setError("");
        setSaved("");
        setPublishedNotice("");
        try {
          const result = await api<{ pending: PendingChanges }>(
            `/api/sites/${siteId}/content-fields`,
            { retries: 2, ...payload("PATCH", {
              stage: true,
              updates: { [field.key]: value },
              frames: frame ? { [field.key]: frame } : undefined,
              definitions: field.auto
                ? [
                    {
                      key: field.key,
                      label: field.label,
                      type: field.type,
                      group: field.group,
                      defaultValue: field.defaultValue,
                      purpose: field.purpose,
                      i18nKey: field.i18nKey || undefined,
                    },
                  ]
                : undefined,
            }) },
          );
          pendingRef.current = result.pending;
          setPending(result.pending);
          setSaved(field.key);
          paintPreview(field, value, frame ?? result.pending.frames[field.key] ?? null);
          paintAllPending();
        } catch (cause) {
          setError(
            cause instanceof Error ? cause.message : "Salvataggio non riuscito",
          );
        } finally {
          setBusy("");
        }
      };
      const queued = persistQueueRef.current.then(execute, execute);
      persistQueueRef.current = queued;
      await queued;
    },
    [canEdit, siteId],
  );
  useEffect(() => {
    if (!liveUrl) return;
    const expectedOrigin = new URL(liveUrl).origin;
    function receivePreviewMessage(event: MessageEvent) {
      if (
        event.origin !== expectedOrigin ||
        event.source !== previewRef.current?.contentWindow ||
        !event.data ||
        typeof event.data !== "object"
      )
        return;
      if (
        event.data.type === "universe-cms-fields" &&
        Array.isArray(event.data.fields)
      ) {
        const automatic = (event.data.fields as ContentField[]).filter(
          (field) =>
            !isLockedCredit(field) &&
            field.auto === true &&
            /^auto_(?:text|image|background)_[a-z0-9]{6,16}$/.test(field.key) &&
            typeof field.label === "string" &&
            typeof field.group === "string" &&
            (field.purpose === undefined ||
              field.purpose === "content" ||
              field.purpose === "alt" ||
              field.purpose === "link"),
        );
        const staged = pendingRef.current;
        fieldsRef.current = [
          ...fieldsRef.current.filter((field) => !field.auto || !samePath(field.pagePath, currentPath)),
          ...automatic,
        ];
        paintAllPending();
        setFields(fieldsRef.current);
        setDrafts((current) => ({
          ...current,
          ...Object.fromEntries(
            automatic.map((field) => [
              field.key,
              staged.updates[field.key] ?? field.value,
            ]),
          ),
        }));
        setFrames((current) => ({ ...current, ...framesFrom(automatic), ...staged.frames }));
        setLinkKinds((current) => ({
          ...current,
          ...Object.fromEntries(
            automatic
              .filter((field) => field.purpose === "link" && typeof field.value === "string")
              .map((field) => [field.key, current[field.key] || inferLinkKind(String(staged.updates[field.key] ?? field.value))]),
          ),
        }));
      }
      if (event.data.type === "universe-cms-managed-click") {
        setHeroSlug(typeof event.data.slug === "string" ? event.data.slug : "");
        setHeroOpen(true);
      }
      if (event.data.type === "universe-cms-managed" && typeof event.data.count === "number") {
        setManagedCount(event.data.count);
      }
      if (
        event.data.type === "universe-cms-sections" &&
        Array.isArray(event.data.sections)
      ) {
        setPageSections(
          (event.data.sections as PageSection[])
            .filter((item) => item && typeof item.ref === "string" && typeof item.label === "string")
            .slice(0, 60),
        );
      }
      if (event.data.type === "universe-cms-add-section" && typeof event.data.anchorRef === "string") {
        setBuilderRequest({
          nonce: Date.now(), kind: "add", anchorRef: event.data.anchorRef,
          anchorLabel: typeof event.data.anchorLabel === "string" ? event.data.anchorLabel : "",
          afterBlockId: typeof event.data.afterBlockId === "string" ? event.data.afterBlockId : "",
        });
      }
      if (
        event.data.type === "universe-cms-block-action" &&
        typeof event.data.id === "string" &&
        ["edit", "up", "down", "duplicate", "delete"].includes(event.data.action)
      ) {
        setBuilderRequest({ nonce: Date.now(), kind: "block", id: event.data.id, action: event.data.action });
      }
      if (
        event.data.type === "universe-cms-select" &&
        typeof event.data.key === "string"
      ) {
        setSelectedKey(event.data.key);
        if (window.matchMedia("(max-width: 900px)").matches) setPane("work");
        const selected = fields.find(
          (field) => field.key === event.data.key,
        );
        if (selected) {
          setGroup(selected.group);
          window.setTimeout(() => {
            document
              .getElementById(`field-${event.data.key}`)
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 80);
        }
      }
      if (
        event.data.type === "universe-cms-inline-change" &&
        typeof event.data.key === "string" &&
        typeof event.data.value === "string"
      ) {
        const field = fields.find((item) => item.key === event.data.key);
        if (field && (field.type === "text" || field.type === "textarea")) {
          setDrafts((current) => ({
            ...current,
            [field.key]: event.data.value,
          }));
          void persistField(field, event.data.value);
        }
      }
    }
    window.addEventListener("message", receivePreviewMessage);
    return () => window.removeEventListener("message", receivePreviewMessage);
  }, [currentPath, fields, liveUrl, persistField]);
  const pageFields = fields.filter(
    (field) =>
      !isLockedCredit(field) &&
      (!field.auto || samePath(field.pagePath, currentPath)),
  );
  const groups = useMemo(
    () => [...new Set(pageFields.map((field) => field.group))],
    [pageFields],
  );
  const activeGroup = groups.includes(group) ? group : (groups[0] || "Contenuti pagina");
  const visible = pageFields.filter((field) => field.group === activeGroup);
  const menuContext = hasMenu && /menu|carta|piatti|cucina|wine|drink/i.test(
    `${currentPage?.title || ""} ${currentPage?.slug || ""} ${activeGroup}`,
  );
  function openMenuManager() {
    navigate(`/sites/${siteId}?section=menu`);
  }
  async function saveField(
    field: ContentField,
    value = drafts[field.key],
    frame?: PhotoFrame | null,
  ) {
    const next = field.purpose === "link" && typeof value === "string"
      ? normalizeLink(linkKinds[field.key] || inferLinkKind(value), value)
      : value;
    if (field.purpose === "link" && typeof next === "string") {
      setDrafts((current) => ({ ...current, [field.key]: next }));
    }
    await persistField(field, next, frame);
  }
  function editText(field: ContentField, value: string) {
    setDrafts((current) => ({ ...current, [field.key]: value }));
    const previous = textTimers.current[field.key];
    if (previous) window.clearTimeout(previous.timer);
    const run = async () => {
      delete textTimers.current[field.key];
      await persistField(field, value);
    };
    textTimers.current[field.key] = { timer: window.setTimeout(() => void run(), 700), run };
  }
  async function flushText(key?: string) {
    const entries = Object.entries(textTimers.current).filter(([item]) => !key || item === key);
    await Promise.all(
      entries.map(([, entry]) => {
        window.clearTimeout(entry.timer);
        return entry.run();
      }),
    );
  }
  flushTextRef.current = () => flushText();
  async function publishAll() {
    if (!canEdit || publishing) return;
    setPublishing(true);
    setError("");
    setPublishedNotice("");
    let retried = false;
    try {
      await flushText();
      await persistQueueRef.current;
      const result = await api<{ fields: ContentField[]; published: number; pending: PendingChanges }>(
        `/api/sites/${siteId}/content-fields/publish`,
        { ...payload("POST"), retries: 2, onRetry: () => { retried = true; } },
      );
      pendingRef.current = result.pending;
      setPending(result.pending);
      setFields(result.fields);
      setDrafts(Object.fromEntries(result.fields.map((item) => [item.key, item.value])));
      setFrames(framesFrom(result.fields));
      setSaved("");
      setPreviewKey((key) => key + 1);
      setPublishedNotice(
        result.published === 1
          ? "Fatto: 1 modifica è online."
          : `Fatto: ${result.published} modifiche sono online.`,
      );
    } catch (cause) {
      // Se la rete è caduta dopo la pubblicazione, il nuovo tentativo trova la bozza già vuota: è andata a buon fine.
      if (retried && cause instanceof ApiError && cause.status === 409 && cause.message.startsWith("Non ci sono modifiche")) {
        try {
          const fresh = await api<{ fields: ContentField[]; pending?: PendingChanges }>(`/api/sites/${siteId}/content-fields`);
          pendingRef.current = fresh.pending || NO_PENDING;
          setPending(pendingRef.current);
          setFields(fresh.fields);
          setDrafts(Object.fromEntries(fresh.fields.map((item) => [item.key, pendingRef.current.updates[item.key] ?? item.value])));
          setFrames({ ...framesFrom(fresh.fields), ...pendingRef.current.frames });
          setPreviewKey((key) => key + 1);
          setPublishedNotice("Fatto: le modifiche sono online.");
          return;
        } catch {
          // Se anche la verifica fallisce, mostra l'errore originale.
        }
      }
      setError(cause instanceof Error ? cause.message : "Pubblicazione non riuscita");
    } finally {
      setPublishing(false);
    }
  }
  async function discard(field?: ContentField) {
    if (!canEdit) return;
    if (
      !field &&
      !window.confirm("Annullare tutte le modifiche non pubblicate? Il sito online resta com’è.")
    )
      return;
    await flushText(field?.key);
    await persistQueueRef.current;
    setError("");
    try {
      const result = await api<{ pending: PendingChanges }>(
        `/api/sites/${siteId}/content-fields/draft${field ? `?key=${encodeURIComponent(field.key)}` : ""}`,
        { ...payload("DELETE"), retries: 2 },
      );
      pendingRef.current = result.pending;
      setPending(result.pending);
      const reset = field ? [field] : fieldsRef.current;
      setDrafts((current) => ({
        ...current,
        ...Object.fromEntries(reset.map((item) => [item.key, item.value])),
      }));
      setFrames((current) => ({ ...current, ...framesFrom(reset) }));
      if (field) {
        paintPreview(field, field.value, readFrame(field.frame));
      } else {
        setPreviewKey((key) => key + 1);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Annullamento non riuscito");
    }
  }
  function updateFrame(field: ContentField, frame: PhotoFrame, publish = false) {
    setFrames((current) => ({ ...current, [field.key]: frame }));
    const value = drafts[field.key];
    if (typeof value === "string") paintPreview(field, value, frame);
    if (publish) void saveField(field, value, frame);
  }
  function dragFrame(field: ContentField, event: React.PointerEvent<HTMLDivElement>) {
    if (!canEdit || !drafts[field.key]) return;
    const box = event.currentTarget;
    const rect = box.getBoundingClientRect();
    const startX = event.clientX;
    const startY = event.clientY;
    const origin = frames[field.key] || { x: 50, y: 50, ratio: "site" as const };
    box.setPointerCapture(event.pointerId);
    const move = (ev: PointerEvent) => {
      const x = Math.min(100, Math.max(0, Math.round(origin.x - ((ev.clientX - startX) / rect.width) * 100)));
      const y = Math.min(100, Math.max(0, Math.round(origin.y - ((ev.clientY - startY) / rect.height) * 100)));
      updateFrame(field, { ...origin, x, y });
    };
    const end = (ev: PointerEvent) => {
      box.removeEventListener("pointermove", move);
      box.removeEventListener("pointerup", end);
      const x = Math.min(100, Math.max(0, Math.round(origin.x - ((ev.clientX - startX) / rect.width) * 100)));
      const y = Math.min(100, Math.max(0, Math.round(origin.y - ((ev.clientY - startY) / rect.height) * 100)));
      updateFrame(field, { ...origin, x, y }, true);
    };
    box.addEventListener("pointermove", move);
    box.addEventListener("pointerup", end);
  }
  async function upload(field: ContentField, file: File) {
    if (!canEdit) return;
    setBusy(field.key);
    setError("");
    try {
      const result = { media: await uploadMedia<MediaAsset>(siteId, file, field.label) };
      if (field.type === "gallery") {
        const next = [
          ...(Array.isArray(drafts[field.key])
            ? (drafts[field.key] as string[])
            : []),
          result.media.url,
        ];
        setDrafts((current) => ({ ...current, [field.key]: next }));
        await saveField(field, next);
      } else {
        setDrafts((current) => ({ ...current, [field.key]: result.media.url }));
        await saveField(field, result.media.url, frames[field.key] || null);
      }
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Caricamento non riuscito",
      );
      setBusy("");
    }
  }
  async function openMediaLibrary(field: ContentField) {
    setError("");
    setMediaTargetKey(field.key);
    setMediaSearch("");
    try {
      const result = await api<{ media: MediaAsset[] }>(`/api/sites/${siteId}/media`);
      setMedia(result.media.filter((item) => item.mimeType.startsWith("image/")));
    } catch (cause) {
      setMediaTargetKey("");
      setError(cause instanceof Error ? cause.message : "Libreria immagini non disponibile");
    }
  }
  async function chooseMedia(item: MediaAsset) {
    const field = fields.find((candidate) => candidate.key === mediaTargetKey);
    if (!field) return;
    setMediaTargetKey("");
    if (field.type === "gallery") {
      const next = [
        ...(Array.isArray(drafts[field.key]) ? (drafts[field.key] as string[]) : []),
        item.url,
      ];
      setDrafts((current) => ({ ...current, [field.key]: next }));
      await saveField(field, next);
      return;
    }
    setDrafts((current) => ({ ...current, [field.key]: item.url }));
    await saveField(field, item.url, frames[field.key] || null);
  }
  function uploadDropped(field: ContentField, files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Trascina un file immagine JPG, PNG, WebP o AVIF.");
      return;
    }
    void upload(field, file);
  }
  async function restore(field: ContentField) {
    if (!canEdit) return;
    if (
      field.previousValue === null ||
      !window.confirm(
        `Ripristinare la versione precedente di “${field.label}”?`,
      )
    )
      return;
    await saveField(field, field.previousValue);
  }
  async function openVersions() {
    setError("");
    try {
      const result = await api<{ versions: ContentVersion[] }>(
        `/api/sites/${siteId}/content-versions`,
      );
      setVersions(result.versions);
      setShowVersions(true);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Cronologia non disponibile",
      );
    }
  }
  async function restoreVersion(version: ContentVersion) {
    if (!canEdit) return;
    if (
      !window.confirm(
        `Ripristinare la versione ${version.number}? Il contenuto attuale resterà nella cronologia.`,
      )
    )
      return;
    setBusy("history");
    try {
      const result = await api<{ fields: ContentField[] }>(
        `/api/sites/${siteId}/content-versions/${version.id}/restore`,
        payload("POST"),
      );
      setFields(result.fields);
      setDrafts(
        Object.fromEntries(
          result.fields.map((item) => [item.key, pendingRef.current.updates[item.key] ?? item.value]),
        ),
      );
      setShowVersions(false);
      setPreviewKey((key) => key + 1);
      setSaved("history");
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Ripristino non riuscito",
      );
    } finally {
      setBusy("");
    }
  }
  if (error && !fields.length)
    return (
      <main className="safe-editor-state">
        <img src={universeMark} alt="Universe" />
        <h1>Collegamento in corso</h1>
        <p>{error}</p>
        <button onClick={() => navigate(`/sites/${siteId}`)}>
          Torna al sito
        </button>
      </main>
    );
  return (
    <main className={`safe-editor pane-${pane}`}>
      <header>
        <button
          className="safe-brand"
          onClick={() => navigate(`/sites/${siteId}`)}
        >
          <img src={universeMark} alt="" />
          <strong>UNIVERSE</strong>
        </button>
        <div>
          <strong>{siteName || "Contenuti sito"}</strong>
          <small>{currentPage?.title || "Modifica protetta"}</small>
        </div>
        <div className="safe-pane-switch" role="group" aria-label="Vista di lavoro">
          <button
            type="button"
            className={pane === "work" ? "active" : ""}
            aria-pressed={pane === "work"}
            onClick={() => setPane("work")}
          >
            Editor
          </button>
          <button
            type="button"
            className={pane === "preview" ? "active" : ""}
            aria-pressed={pane === "preview"}
            onClick={() => setPane("preview")}
          >
            Anteprima
          </button>
        </div>
        {!canEdit && <span>Modalità sola lettura</span>}
        <button className="safe-history-btn" onClick={openVersions}>
          Cronologia
        </button>
        <a className="safe-open-live" href={publicUrl || liveUrl} target="_blank" rel="noreferrer">
          Sito online ↗
        </a>
        {canEdit && (
          <div className={`safe-publish ${pending.count ? "has-pending" : "is-clean"}`} aria-live="polite">
            <p>
              {publishing ? (
                <strong>Pubblicazione in corso…</strong>
              ) : pending.count ? (
                <>
                  <strong>
                    {pending.count === 1 ? "1 modifica da pubblicare" : `${pending.count} modifiche da pubblicare`}
                  </strong>
                  <small>Le vedi solo nell’anteprima</small>
                </>
              ) : (
                <>
                  <strong>Tutto online</strong>
                  <small>{publishedNotice || "Il sito è aggiornato"}</small>
                </>
              )}
            </p>
            {pending.count > 0 && !publishing && (
              <button type="button" className="safe-discard" onClick={() => void discard()}>
                Annulla
              </button>
            )}
            <button
              type="button"
              className="safe-publish-btn"
              disabled={!pending.count || publishing}
              onClick={() => void publishAll()}
            >
              {publishing ? "Pubblico…" : "Pubblica"}
              {pending.count > 0 && !publishing && <span>{pending.count}</span>}
            </button>
          </div>
        )}
      </header>
      <div className="safe-body">
        <aside>
          {hasMenu && (
            <button className="safe-menu-jump" type="button" onClick={openMenuManager}>
              Modifica il menu
              <span>Piatti e prezzi</span>
            </button>
          )}
          <p>Pagine del sito</p>
          {pages.map((page) => (
            <button
              key={page.id}
              className={page.id === currentPage?.id ? "active" : ""}
              onClick={() => navigate(`/sites/${siteId}/editor/${page.id}`)}
            >
              {page.isHome ? "Home" : page.title}
              <span>{page.isHome ? "/" : page.id.startsWith("exp-") ? "Esperienza · aggiungi sezioni" : `/${page.slug}`}</span>
            </button>
          ))}
          {hasEscape && managedCount > 0 && (
            <>
              <button className="safe-menu-jump" type="button" onClick={() => setHeroOpen((open) => !open)}>
                Contenuti dell’esperienza
                <span>Titolo, testi, prezzi, foto, video e stato sul sito</span>
              </button>
              {heroOpen && (
                <HeroQuickEditor
                  siteId={siteId}
                  canEdit={canEdit}
                  initialSlug={heroSlug}
                  onSaved={() => setPreviewKey((key) => key + 1)}
                  onClose={() => setHeroOpen(false)}
                />
              )}
            </>
          )}
          <p>Sezioni modificabili</p>
          {groups.map((item) => (
            <button
              key={item}
              className={activeGroup === item ? "active" : ""}
              onClick={() => setGroup(item)}
            >
              {item}
              <span>
                {pageFields.filter((field) => field.group === item).length}
              </span>
            </button>
          ))}
        </aside>
        <section className="safe-preview">
          <div className="safe-preview-bar">
            <span>
              {pending.count
                ? "Anteprima con le modifiche non ancora pubblicate"
                : liveConnected
                  ? "Anteprima del sito online"
                  : "Anteprima di modifica · dominio da collegare"}
            </span>
            <div className="safe-preview-tools">
              {(["desktop", "tablet", "mobile"] as const).map((item) => (
                <button
                  key={item}
                  className={viewport === item ? "active" : ""}
                  onClick={() => setViewport(item)}
                  aria-pressed={viewport === item}
                >
                  {item === "desktop"
                    ? "Desktop"
                    : item === "tablet"
                      ? "Tablet"
                      : "Mobile"}
                </button>
              ))}
              <button onClick={() => setPreviewKey((key) => key + 1)}>
                Aggiorna ↻
              </button>
            </div>
          </div>
          {liveUrl && (
            <div className={`safe-preview-frame viewport-${viewport}`}>
              <iframe
                ref={previewRef}
                key={previewKey}
                src={`${liveUrl.replace(/\/$/, "")}${currentPath}${currentPath.includes("?") ? "&" : "?"}cms_preview=${previewKey}&cms_mode=${canEdit ? "edit" : "view"}`}
                title={`Anteprima ${siteName}`}
                onLoad={() => {
                  window.setTimeout(() => paintAllPending(), 80);
                  window.setTimeout(() => paintAllPending(), 400);
                }}
              />
            </div>
          )}
        </section>
        <aside className={`safe-fields${builderActive ? " builder-open" : ""}`}>
          <div className="safe-fields-head">
            <small>{activeGroup}</small>
            <h1>Modifica contenuti</h1>
            <p>
              Clicca un testo o una foto nell’anteprima, oppure scegli una
              sezione. Le modifiche appaiono subito nell’anteprima e vanno
              online solo quando premi <strong>Pubblica</strong>.
            </p>
          </div>
          {!liveConnected && (
            <div className="notice">
              Puoi modificare tutti i contenuti. Il dominio pubblico usa ancora
              una vecchia origine Cloudflare: le modifiche saranno visibili lì
              appena Universe avrà accesso alla relativa zona.
            </div>
          )}
          {hasMenu && (
            <button type="button" className={`safe-menu-banner${menuContext ? " is-hot" : ""}`} onClick={openMenuManager}>
              <strong>{menuContext ? "Stai toccando il menu del sito" : "Devi cambiare piatti o prezzi?"}</strong>
              <span>Apri la modifica del menu →</span>
            </button>
          )}
          {error && <div className="notice error">{error}</div>}
          <SectionBuilder
            siteId={siteId}
            canEdit={canEdit}
            currentPath={currentPath}
            sections={pageSections}
            request={builderRequest}
            onActiveChange={setBuilderActive}
            onPreview={(blocks: ContentBlock[], focus?: string) => {
              const origin = liveUrlRef.current;
              const win = previewRef.current?.contentWindow;
              if (origin && win) win.postMessage({ type: "universe-cms-apply-blocks", blocks, focus: focus || "" }, new URL(origin).origin);
            }}
          />
          {canEdit && pending.count > 0 && (
            <div className="safe-publish-strip">
              <div>
                <strong>{pending.count === 1 ? "1 modifica pronta" : `${pending.count} modifiche pronte`}</strong>
                <span>Le vedi nell’anteprima. Online solo dopo Pubblica.</span>
              </div>
              <button type="button" className="safe-publish-btn" disabled={publishing} onClick={() => void publishAll()}>
                {publishing ? "Pubblico…" : "Pubblica"}
                {!publishing && <span>{pending.count}</span>}
              </button>
            </div>
          )}
          {visible.map((field) => (
            <article
              id={`field-${field.key}`}
              key={field.key}
              className={selectedKey === field.key ? "selected" : ""}
            >
              <div className="safe-field-label">
                <strong>
                  {field.purpose === "link" ? "Collegamento" : field.purpose === "alt" ? "Descrizione immagine" : field.label}
                  {field.purpose === "link" && <small className="safe-field-kind">Pulsante o link</small>}
                  {field.purpose === "alt" && <small className="safe-field-kind">Accessibilità e Google</small>}
                </strong>
                <div>
                  {canEdit &&
                    (field.type === "text" || field.type === "textarea") &&
                    typeof field.defaultValue === "string" &&
                    field.defaultValue !== String(drafts[field.key] ?? "") && (
                      <button
                        className="safe-restore"
                        onClick={() => {
                          setDrafts((current) => ({ ...current, [field.key]: field.defaultValue! }));
                          void saveField(field, field.defaultValue);
                        }}
                      >
                        Originale
                      </button>
                    )}
                  {canEdit && field.previousValue !== null && !(field.key in pending.updates) && (
                    <button
                      className="safe-restore"
                      onClick={() => restore(field)}
                    >
                      Versione precedente
                    </button>
                  )}
                  {canEdit && (field.key in pending.updates || field.key in pending.frames) && (
                    <>
                      <span className="safe-pending-tag">Da pubblicare</span>
                      <button className="safe-restore" onClick={() => void discard(field)}>
                        Annulla
                      </button>
                    </>
                  )}
                </div>
              </div>
              {field.type === "text" && field.purpose !== "link" && (
                <input
                  disabled={!canEdit}
                  value={String(drafts[field.key] || "")}
                  aria-label={field.label}
                  onChange={(event) => editText(field, event.target.value)}
                  onBlur={() => void flushText(field.key)}
                  onKeyDown={(event) => {
                    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") void flushText(field.key);
                  }}
                />
              )}
              {field.type === "text" && field.purpose !== "link" && (
                <div className="safe-writing-meta">
                  <span>{String(drafts[field.key] || "").length} caratteri</span>
                  {busy === field.key ? <em>Aggiorno l’anteprima…</em> : null}
                </div>
              )}
              {field.purpose === "link" && typeof drafts[field.key] === "string" && (() => {
                const kind = linkKinds[field.key] || inferLinkKind(String(drafts[field.key]));
                const raw = linkInputValue(kind, String(drafts[field.key]));
                const problem = linkProblem(kind, raw);
                const normalized = normalizeLink(kind, raw);
                return <div className="safe-link-editor">
                  <label>
                    Che cosa deve aprire?
                    <select
                      disabled={!canEdit}
                      value={kind}
                      onChange={(event) => {
                        const nextKind = event.target.value as LinkKind;
                        setLinkKinds((current) => ({ ...current, [field.key]: nextKind }));
                        setDrafts((current) => ({ ...current, [field.key]: "" }));
                      }}
                    >
                      <option value="page">Una pagina del sito</option>
                      <option value="section">Una sezione della pagina</option>
                      <option value="web">Un altro sito web</option>
                      <option value="email">Un indirizzo email</option>
                      <option value="phone">Un numero di telefono</option>
                      <option value="whatsapp">Una chat WhatsApp</option>
                    </select>
                  </label>
                  <label>
                    Destinazione
                    <input
                      disabled={!canEdit}
                      value={raw}
                      list={kind === "page" ? `pages-${field.key}` : undefined}
                      placeholder={linkPlaceholder(kind)}
                      aria-invalid={Boolean(problem)}
                      onChange={(event) => setDrafts((current) => ({ ...current, [field.key]: event.target.value }))}
                      onKeyDown={(event) => {
                        if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && !problem) void saveField(field);
                      }}
                    />
                    {kind === "page" && <datalist id={`pages-${field.key}`}>
                      {pages.map((page) => <option key={page.id} value={page.isHome ? "/" : `/${page.slug}`}>{page.title}</option>)}
                    </datalist>}
                  </label>
                  {problem ? <p className="safe-link-error">{problem}</p> : <p className="safe-link-ready">Destinazione pronta: <code>{normalized}</code></p>}
                  <div className="safe-link-actions">
                    <button className="safe-save" type="button" disabled={!canEdit || Boolean(problem) || busy === field.key} onClick={() => saveField(field)}>
                      {busy === field.key ? "Aggiorno…" : "Applica collegamento"}
                    </button>
                    {!problem && <a href={normalized} target="_blank" rel="noreferrer">Prova link ↗</a>}
                  </div>
                </div>;
              })()}
              {field.type === "textarea" && (
                <textarea
                  disabled={!canEdit}
                  value={String(drafts[field.key] || "")}
                  onChange={(event) => editText(field, event.target.value)}
                  onBlur={() => void flushText(field.key)}
                  onKeyDown={(event) => {
                    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") void flushText(field.key);
                  }}
                />
              )}
              {field.type === "textarea" && (
                <div className="safe-writing-meta">
                  <span>{String(drafts[field.key] || "").length} caratteri</span>
                  {busy === field.key ? <em>Aggiorno l’anteprima…</em> : null}
                </div>
              )}
              {field.i18nKey && (field.type === "text" || field.type === "textarea") && field.purpose !== "link" && (
                <p className="safe-i18n-note">Scrivi in italiano: inglese, tedesco e francese si traducono da soli quando pubblichi.</p>
              )}
              {field.type === "image" && (
                <>
                  <div
                    className={`safe-image-preview ${frames[field.key]?.ratio || "site"}`}
                    onPointerDown={(event) => dragFrame(field, event)}
                  >
                    {drafts[field.key] ? (
                      <img
                        src={absoluteMedia(String(drafts[field.key]), liveUrl)}
                        alt={field.label}
                        style={{
                          objectPosition: `${frames[field.key]?.x ?? 50}% ${frames[field.key]?.y ?? 50}%`,
                        }}
                      />
                    ) : (
                      <span>Nessuna immagine</span>
                    )}
                  </div>
                  {canEdit && drafts[field.key] && (
                    <>
                      <p className="safe-frame-hint">Trascina la foto per scegliere cosa resta inquadrato.</p>
                      <div className="safe-frame-tools">
                        <button
                          type="button"
                          className={!frames[field.key] || frames[field.key]?.ratio === "site" ? "active" : ""}
                          onClick={() =>
                            updateFrame(
                              field,
                              { x: frames[field.key]?.x ?? 50, y: frames[field.key]?.y ?? 50, ratio: "site" },
                              true,
                            )
                          }
                        >
                          Originale
                        </button>
                        <button
                          type="button"
                          className={frames[field.key]?.ratio === "landscape" ? "active" : ""}
                          onClick={() =>
                            updateFrame(
                              field,
                              { x: frames[field.key]?.x ?? 50, y: frames[field.key]?.y ?? 50, ratio: "landscape" },
                              true,
                            )
                          }
                        >
                          Orizzontale
                        </button>
                        <button
                          type="button"
                          className={frames[field.key]?.ratio === "portrait" ? "active" : ""}
                          onClick={() =>
                            updateFrame(
                              field,
                              { x: frames[field.key]?.x ?? 50, y: frames[field.key]?.y ?? 50, ratio: "portrait" },
                              true,
                            )
                          }
                        >
                          Verticale
                        </button>
                      </div>
                    </>
                  )}
                  {canEdit && <div className="safe-image-actions">
                    <label
                      className="safe-file"
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => {
                        event.preventDefault();
                        uploadDropped(field, event.dataTransfer.files);
                      }}
                    >
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/avif,.jpg,.jpeg,.png,.webp,.avif"
                        onChange={(event) => {
                          uploadDropped(field, event.target.files);
                          event.target.value = "";
                        }}
                      />
                      <span>{busy === field.key ? "Caricamento…" : "Sostituisci foto"}</span>
                      <small>Clicca o trascina qui · JPG, PNG, WebP o AVIF · max 5 MB</small>
                    </label>
                    <button className="safe-media-library-btn" type="button" onClick={() => void openMediaLibrary(field)}>
                      Scegli dalla libreria
                    </button>
                  </div>}
                </>
              )}
              {field.type === "gallery" && (
                <>
                  <div className="safe-gallery">
                    {(Array.isArray(drafts[field.key])
                      ? (drafts[field.key] as string[])
                      : []
                    ).map((item, index) => (
                      <div key={`${item}-${index}`}>
                        {field.mediaType !== "video" && (
                          <img
                            src={absoluteMedia(item, liveUrl)}
                            alt={`${field.label} ${index + 1}`}
                          />
                        )}
                        <small>
                          {field.mediaType === "video"
                            ? item
                            : `Foto ${index + 1}`}
                        </small>
                        {canEdit && <div className="safe-gallery-actions">
                          <button
                            type="button"
                            disabled={index === 0}
                            aria-label={`Sposta Foto ${index + 1} indietro`}
                            onClick={() => {
                              const next = [...(drafts[field.key] as string[])];
                              [next[index - 1], next[index]] = [next[index], next[index - 1]];
                              setDrafts((current) => ({ ...current, [field.key]: next }));
                              void saveField(field, next);
                            }}
                          >
                            ←
                          </button>
                          <button
                            type="button"
                            disabled={index === (drafts[field.key] as string[]).length - 1}
                            aria-label={`Sposta Foto ${index + 1} avanti`}
                            onClick={() => {
                              const next = [...(drafts[field.key] as string[])];
                              [next[index], next[index + 1]] = [next[index + 1], next[index]];
                              setDrafts((current) => ({ ...current, [field.key]: next }));
                              void saveField(field, next);
                            }}
                          >
                            →
                          </button>
                          <button
                            type="button"
                            className="remove"
                            onClick={() => {
                              const next = (drafts[field.key] as string[]).filter(
                                (_, itemIndex) => itemIndex !== index,
                              );
                              setDrafts((current) => ({ ...current, [field.key]: next }));
                              void saveField(field, next);
                            }}
                          >
                            Rimuovi
                          </button>
                        </div>}
                      </div>
                    ))}
                  </div>
                  {canEdit && field.mediaType !== "video" && (
                    <div className="safe-image-actions">
                      <label
                        className="safe-file"
                        onDragOver={(event) => event.preventDefault()}
                        onDrop={(event) => {
                          event.preventDefault();
                          uploadDropped(field, event.dataTransfer.files);
                        }}
                      >
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/avif"
                          onChange={(event) => {
                            uploadDropped(field, event.target.files);
                            event.target.value = "";
                          }}
                        />
                        <span>Aggiungi foto</span>
                        <small>Clicca o trascina qui</small>
                      </label>
                      <button className="safe-media-library-btn" type="button" onClick={() => void openMediaLibrary(field)}>
                        Scegli dalla libreria
                      </button>
                    </div>
                  )}
                </>
              )}
              {(field.type === "image" || field.type === "gallery") &&
                busy === field.key && (
                  <span className="safe-uploading">Caricamento…</span>
                )}
              {(field.type === "image" || field.type === "gallery") &&
                saved === field.key &&
                busy !== field.key &&
                field.key in pending.updates && (
                  <span className="safe-published">
                    {field.type === "gallery"
                      ? "Galleria aggiornata: la vedrai sul sito dopo Pubblica."
                      : "Foto nell’anteprima. Premi Pubblica per metterla online."}
                  </span>
                )}
            </article>
          ))}
        </aside>
      </div>
      {showVersions && (
        <div
          className="modal-backdrop"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setShowVersions(false)
          }
        >
          <section
            ref={historyDialogRef}
            className="modal safe-history-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="history-dialog-title"
            tabIndex={-1}
          >
            <button
              className="modal-close"
              onClick={() => setShowVersions(false)}
              aria-label="Chiudi cronologia"
            >
              ×
            </button>
            <p className="eyebrow indigo">Versioni del sito</p>
            <h2 id="history-dialog-title">Cronologia contenuti</h2>
            <p className="muted">
              Ogni pubblicazione crea una versione completa. Il ripristino
              rimette subito online quella versione, senza toccare layout o
              animazioni.
            </p>
            <div className="safe-version-list">
              {versions.map((version) => (
                <article key={version.id}>
                  <div>
                    <strong>Versione {version.number}</strong>
                    <small>
                      {new Intl.DateTimeFormat("it-IT", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      }).format(new Date(version.createdAt))}{" "}
                      · {version.author}
                    </small>
                    <span>{version.changedFields.join(", ")}</span>
                  </div>
                  {canEdit && <button
                    className="secondary"
                    disabled={busy === "history"}
                    onClick={() => restoreVersion(version)}
                  >
                    Ripristina
                  </button>}
                </article>
              ))}
              {versions.length === 0 && (
                <p>
                  Nessuna versione salvata. La prima verrà creata alla prossima
                  pubblicazione.
                </p>
              )}
            </div>
          </section>
        </div>
      )}
      {mediaTargetKey && (
        <div
          className="modal-backdrop"
          onMouseDown={(event) =>
            event.target === event.currentTarget && setMediaTargetKey("")
          }
        >
          <section
            ref={mediaDialogRef}
            className="modal safe-media-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="media-dialog-title"
            tabIndex={-1}
          >
            <button
              className="modal-close"
              onClick={() => setMediaTargetKey("")}
              aria-label="Chiudi libreria immagini"
            >
              ×
            </button>
            <p className="eyebrow indigo">Immagini già caricate</p>
            <h2 id="media-dialog-title">Libreria immagini</h2>
            <p className="muted">Riutilizza una foto senza caricarla di nuovo.</p>
            <input
              className="safe-media-search"
              type="search"
              value={mediaSearch}
              onChange={(event) => setMediaSearch(event.target.value)}
              placeholder="Cerca per nome o descrizione"
              aria-label="Cerca nella libreria immagini"
            />
            <div className="safe-media-grid">
              {media
                .filter((item) =>
                  `${item.filename} ${item.altText || ""}`
                    .toLowerCase()
                    .includes(mediaSearch.trim().toLowerCase()),
                )
                .map((item) => (
                  <button type="button" key={item.id} onClick={() => void chooseMedia(item)}>
                    <img src={absoluteMedia(item.url, liveUrl)} alt={item.altText || item.filename} />
                    <span>{item.altText || item.filename}</span>
                  </button>
                ))}
            </div>
            {media.length === 0 && <p className="safe-media-empty">Nessuna immagine ancora disponibile.</p>}
          </section>
        </div>
      )}
    </main>
  );
}
